import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import FormSubHeading from "../components/FormSubHeading";
import { Dialog } from "primereact/dialog";
import { useEffect, useState } from "react";
import {
  // useMedicalCenterClientRequirementsList,
  useMedicalCenterFund,
  useRequirementTypes,
  useRequirementsCompanies,
  useGetInsurance,
  useCentersFromRequirements,
  useCachedRequirementsByClientAndCenter,
  useClientList,
} from "../hooks/queries";
import { MultiSelect } from "primereact/multiselect";
import FundStatusTag from "../components/FundStatusTag";
import { SelectButton } from "primereact/selectbutton";

import { getCachedRequirementList } from "../services/requirementCached";
import { getRequirementsByClient } from "../services/requirementMedicalCenterCache";

/**
 * @typedef TableRequirement Structure of each requirement shown on the table
 * @property {number} requirementType
 * @property {string} requirementTypeLabel
 * @property {number} requirement
 * @property {string} requirementLabel
 * @property {number} medicalCenter
 * @property {string} medicalCenterLabel
 */

/**
 * Modal para registrar un requerimiento
 * @param {object} props
 * @param {boolean} props.showModal
 * @param {() => void} props.onHide
 * @param {React.Dispatch<React.SetStateAction<TableRequirement[] | null>>} props.setRequirementList
 * @param {React.Dispatch<React.SetStateAction<boolean>>} props.setInputsLocked
 * @param {number} props.patientClient
 * @param {{ requirementType: number, requirement: number } | undefined} props.initialValues
 * @param {number | null} props.centerSelected
 * @param {React.Dispatch<React.SetStateAction<number | null>>} props.setCenterSelected
 */
export default function RequirementModal({
  showModal,
  onHide,
  setRequirementList,
  setInputsLocked,
  initialValues,
  patientClient,
  centerSelected,
  setCenterSelected,
  state,
}) {
  // Form state
  const [requirementType, setRequirementType] = useState(null);
  const [requirement, setRequirement] = useState([]);
  const [requirementIds, setRequirementIds] = useState([]);
  const [medicalCenterLabel, setMedicalCenterLabel] = useState("");
  const [medicalCenter, setMedicalCenter] = useState(null);
  const [isSubAdministrator, setIsSubAdministrator] = useState(false);
  const [hasSeenDentist, setHasSeenDentist] = useState(false);
  const [subAdminMedicalCenter, setSubAdminMedicalCenter] = useState(null);

  // Insolvent dialog
  const [insolventMedicalCenter, setInsolventMedicalCenter] = useState(null);

  // console.log({ requirement });

  // Set initial form values on props change
  useEffect(
    function setInitialFormValues() {
      if (initialValues) {
        setRequirementType(initialValues.requirementType);
        setRequirement(initialValues.requirement);
      }
    },
    [initialValues]
  );
  const useInsurance = useGetInsurance();
  const insurance = useInsurance?.data;

  // Check all fields are set to allow saving
  const canSave =
    requirement !== null &&
    requirement.length > 0 &&
    (medicalCenter !== null || centerSelected !== null) &&
    !(isSubAdministrator && hasSeenDentist && !subAdminMedicalCenter);

  // Do necessary queries
  const requirementTypesQuery = useRequirementTypes();

  // const requirementsQuery = useRequirements(requirementType);
  const requirementsQuery = useRequirementsCompanies(
    requirementType,
    [patientClient],
    state
  );

  const medicalCenterFundQuery = useMedicalCenterFund({
    clientID: patientClient,
    state: state,
    select: (data) => {
      return data.map((item) => ({
        id: item.medical_center,
        name: item.medical_center_name,
        ...item,
      }));
    },
  });

  // const medicalCentersQuery = useMedicalCenters({clientID:null, enabled: true});

  // @deprecated This query requests the database, takes time to load
  // For now it will use this one
  // const requirementMedicalCenters = useMedicalCenterClientRequirementsList({
  //   centerId: medicalCenter,
  //   client: patientClient,
  //   state: state,
  // });

  // @author @hector
  // Retrieve list of clients
  const clientListQuery = useClientList();

  // This query uses the cache, is faster and more efficient
  const requirementMedicalCenters = useCachedRequirementsByClientAndCenter({
    clientId: patientClient,
    centerId: medicalCenter,
    centerName: medicalCenterLabel,
  });

  const subAdminMedicalCenters = useCentersFromRequirements({
    subAdministrator: medicalCenter,
    requirements: requirementIds,
    enabled: medicalCenter !== null && isSubAdministrator && hasSeenDentist,
  });

  // Based on status of centerSelected, determine which requirements will be shown on dropdown
  // let requirementOptions = requirementsQuery.data ?? [];
  let requirementOptions = requirementMedicalCenters.data ?? [];
  // let requirementOptions = clientTypeMedicalCenterRequirements.data ?? [];
  // if (centerSelected) {
  //   requirementOptions = clientTypeMedicalCenterRequirements.data ?? [];
  // }

  useEffect(() => {
    async function preloadRequirementCaches() {
      // Preload the requirement list
      await getCachedRequirementList();

      // Get all clients from the query data
      const clientList = clientListQuery?.data || [];

      // Preload requirements for each client
      await Promise.all(
        clientList.map((client) => getRequirementsByClient(client.id))
      );
    }

    preloadRequirementCaches();
  }, [clientListQuery]);

  /**
   * Save or modify case requirement
   */
  function saveRequirement() {
    // Edit requirement
    console.log("Initial values", initialValues);

    const subAdminCenterInstance = subAdminMedicalCenters.data?.find(
      (center) => center.medical_center === subAdminMedicalCenter
    );

    if (initialValues) {
      setRequirementList((prev) => {
        const newList =
          prev?.map((req) => {
            // If has found requirement to edit
            if (req.requirement === initialValues.requirement) {
              return {
                // Requirement type
                requirementType,
                requirementTypeLabel: requirementTypesQuery.data.find(
                  (type) => type.id === requirementType
                ).name,
                // Requirement
                requirement: requirement[0],
                requirementLabel: requirementsQuery.data.find(
                  (r) => r.id === requirement[0]
                ).name,
                // Medical center
                medicalCenter: Number(medicalCenter),
                medicalCenterLabel,
                // Sub administrator medical center
                subAdminMedicalCenter:
                  isSubAdministrator && hasSeenDentist
                    ? Number(subAdminMedicalCenter)
                    : null,
                subAdminMedicalCenterLabel:
                  isSubAdministrator && hasSeenDentist
                    ? String(subAdminCenterInstance?.medical_center_name)
                    : null,
              };
            }
            return req;
          }) ?? [];
        return newList;
      });
    } else {
      // Add new requirement
      if (!centerSelected) {
        setCenterSelected(medicalCenter);
      }
      console.log("Requerimientos", requirement);
      const newRequirements = requirement.map((req) => {
        const reqSelected = requirementMedicalCenters.data.find(
          (reqList) => Number(req) === Number(reqList.id)
        );
        console.log("reqSelected", reqSelected);
        return {
          requirementType: reqSelected?.type_requirement,
          requirementTypeLabel: String(reqSelected?.type_requirement_name),
          requirement: Number(reqSelected.requirement),
          requirementLabel: String(reqSelected?.requirement_name),
          medicalCenter: Number(medicalCenter),
          medicalCenterLabel: String(medicalCenterLabel),
          // Sub administrator medical center
          subAdminMedicalCenter:
            isSubAdministrator && hasSeenDentist
              ? Number(subAdminMedicalCenter)
              : null,
          subAdminMedicalCenterLabel:
            isSubAdministrator && hasSeenDentist
              ? String(subAdminCenterInstance?.medical_center_name)
              : null,
        };
      });
      console.log("NewRequirements", newRequirements);
      // setRequirementList(newRequirements);
      setRequirementList((prev) => {
        const newList = [...prev, ...newRequirements];
        return newList;
      });
    }
    setRequirementType(null);
    setRequirement([]);
    setInputsLocked("all");
    onHide();
  }

  const medicalCenterOptionTemplate = (option) => {
    if (option.company_client__fund_type === "postpay") {
      return (
        <div className="flex align-items-center">
          <div>{option.medical_center_name} </div>
        </div>
      );
    }
    return (
      <div className="flex align-items-center">
        <div>{option.medical_center_name} </div>
        {/* amount_available: "2101.62"
          amount_available_ref: 24.25 
          */}
        {insurance?.name === "Vértice Seguros" &&
          option?.amount_available <= 100 && (
            <FundStatusTag value="agotado" className="text-sm ml-2" />
          )}
        {insurance?.name !== "Vértice Seguros" &&
          option?.amount_available_ref <= 100 && (
            <FundStatusTag value="agotado" className="text-sm ml-2" />
          )}
      </div>
    );
  };

  const optionDisabled = (option) => {
    if (option.company_client__fund_type === "postpay"){
      return false;
    }
    if (insurance?.name === "Vértice Seguros") {
      return option?.amount_available <= 100;
    }
    return option?.amount_available_ref <= 100;
  };

  return (
    <Dialog
      header="Nuevo servicio"
      visible={showModal}
      onHide={onHide}
      draggable={false}
      style={{ width: "95vw" }}
      contentClassName="p-fluid"
    >
      <FormSubHeading title="Ubicación" />
      <div className="p-field">
        {centerSelected !== null ? (
          <div className="p-field">
            <span>
              <b>Centro médico:</b> {medicalCenterLabel ?? ""}
            </span>
          </div>
        ) : (
          <>
            <label htmlFor="medicalCenter">Centro médico</label>
            <Dropdown
              id="dropdown-medical-center"
              inputId="medicalCenter"
              name="medicalCenter"
              disabled={
                medicalCenterFundQuery.isLoading || centerSelected !== null
              }
              value={medicalCenter}
              onChange={(e) => {
                const medicalCenterInstance = medicalCenterFundQuery.data?.find(
                  (center) => center.id === e.value
                );

                if (medicalCenterInstance?.is_insolvent) {
                  setInsolventMedicalCenter(medicalCenterInstance);
                } else {
                  setMedicalCenter(e.value);
                  setMedicalCenterLabel(medicalCenterInstance?.name ?? "");
                  setIsSubAdministrator(
                    medicalCenterInstance?.is_sub_administrator
                  );
                }
              }}
              options={medicalCenterFundQuery.data ?? []}
              optionValue="id"
              optionLabel="name"
              optionDisabled={optionDisabled}
              loading={medicalCenterFundQuery.isLoading}
              placeholder="Seleccionar centro médico"
              emptyMessage="No se hallaron centros médicos para su búsqueda"
              filter
              itemTemplate={medicalCenterOptionTemplate}
              showClear
            />
          </>
        )}
      </div>

      <div className="p-field">
        <label htmlFor="requirement">Servicios</label>
        <MultiSelect
          id="multiselect-requirements"
          inputId="requirements"
          name="requirements"
          placeholder="Seleccionar servicios"
          value={requirement}
          onChange={(e) => {
            setRequirement(e.value);
            const selectedRequirements = e.value.map(
              (req) =>
                requirementMedicalCenters.data.find((r) => r.id === req)
                  ?.requirement
            );
            setRequirementIds(selectedRequirements);
          }}
          loading={requirementMedicalCenters.isLoading}
          disabled={requirementMedicalCenters.isLoading}
          options={requirementOptions}
          optionValue="id"
          optionLabel="name"
          filter
          virtualScrollerOptions={{ itemSize: 48 }}
          emptyMessage="No se hallaron servicios para su búsqueda"
        />
      </div>

      {isSubAdministrator && (
        <div className="flex flex-row align-items-center gap-3 pt-2 mb-3">
          <span>¿El paciente ya se ha visto con un odontólogo?</span>
          <SelectButton
            value={hasSeenDentist}
            onChange={(e) => setHasSeenDentist(e.value)}
            options={[
              { label: "Si", value: true },
              { label: "No", value: false },
            ]}
            disabled={!isSubAdministrator}
            allowEmpty={false}
          />
        </div>
      )}

      {isSubAdministrator && hasSeenDentist && (
        <div className="p-field mb-2">
          <label htmlFor="subAdminMedicalCenter">Odontólogo</label>
          <Dropdown
            inputId="subAdminMedicalCenter"
            name="subAdminMedicalCenter"
            disabled={subAdminMedicalCenters.isLoading}
            value={subAdminMedicalCenter}
            onChange={(e) => setSubAdminMedicalCenter(e.value)}
            options={subAdminMedicalCenters.data ?? []}
            optionValue="medical_center"
            optionLabel="medical_center_name"
            loading={subAdminMedicalCenters.isLoading}
            placeholder="Seleccionar odontólogo"
            emptyMessage="No se hallaron odontólogos para su búsqueda"
            filter
            showClear
          />
        </div>
      )}
      <Button
        id="btn-save"
        label="Guardar"
        onClick={saveRequirement}
        disabled={!canSave}
      />

      {/* Insolvent medical center dialog */}
      <Dialog
        draggable={false}
        visible={!!insolventMedicalCenter}
        onHide={() => {
          if (insolventMedicalCenter) setInsolventMedicalCenter(null);
        }}
        headerClassName="bg-white pb-0"
        contentClassName="px-4 pt-0 pb-4"
        style={{ width: "45%" }}
      >
        <h1 className="text-center mt-0">Ups 😢</h1>
        <p className="text-lg text-center">
          El proveedor <b>{insolventMedicalCenter?.name}</b> no puede recibir
          casos, ya que se encuentra insolvente en Citamed
        </p>
        <div className="flex justify-content-center">
          <Button
            label="Entendido"
            onClick={() => setInsolventMedicalCenter(false)}
          />
        </div>
      </Dialog>
    </Dialog>
  );
}
